// Who is signed in and which company they work in (WEB_PLAN.md §3.1, §4.6). Mirrors GroundTeam/Sync/SyncCenter.swift
// (sessionChanged, resolveMembership, refreshMembership, handleRemoval, signOut):
// - supabase-js restores the stored session and keeps it fresh; `onAuthStateChange` drives everything here.
// - After a sign-in, `my_memberships` decides: an active membership → ready (SessionCtx); only a suspended one →
//   "Your access was paused"; none → No company.
// - Local company data (IndexedDB replica + outbox, owned by sync/) is wiped on SIGNED_OUT and whenever the data
//   scope (account, company or role) changes, BEFORE the new session reaches any screen. The last scope is kept in
//   localStorage so a reload (or a crash before a wipe) is caught too.
// - A verified code whose password is still being saved doesn't count as signed in yet (pending.ts gate).

import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Membership } from '@/contracts/wire';
import type { SessionCtx } from '@/session/types';
import { supabase } from '@/supabase';
import { wipeAllLocalData } from '@/sync/local';
import { isNetworkError, isSessionFailure, supabaseErrorText } from './errors';
import { flushPending } from './flush';
import { decideMembership, fetchMemberships, sameSessionCtx, scopeKey, sessionCtxFrom } from './memberships';
import { forgetPendingAuth, isFinishingSignIn, onFinishingReleased } from './pending';
import type { AuthState, AuthStatus } from './types';

interface Core {
  status: AuthStatus;
  user: User | null;
  session: SessionCtx | null;
  memberships: Membership[];
  error: string | null;
}

const SIGNED_OUT: Core = { status: 'signedOut', user: null, session: null, memberships: [], error: null };
const LOADING: Core = { status: 'loading', user: null, session: null, memberships: [], error: null };

const noop = async () => {};
const AuthContext = createContext<AuthState>({ ...LOADING, refreshMemberships: noop, signOut: noop });

/** localStorage key of the data scope the local replica belongs to (see memberships.scopeKey). */
const SCOPE_STORAGE_KEY = 'groundteam.console.scope';
/** Re-read memberships (role changes, removal) at most this often when the tab comes back into view. */
const MEMBERSHIP_CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * The scope as last written in this page load, for browsers that refuse storage (blocked site data): without it
 * every membership check would see "no scope", take the console down and wipe it again.
 */
let memoryScope: string | null = null;

function readScope(): string | null {
  try {
    return window.localStorage.getItem(SCOPE_STORAGE_KEY);
  } catch {
    return memoryScope;
  }
}

function writeScope(scope: string | null): void {
  memoryScope = scope;
  try {
    if (scope === null) window.localStorage.removeItem(SCOPE_STORAGE_KEY);
    else window.localStorage.setItem(SCOPE_STORAGE_KEY, scope);
  } catch {
    // No storage: the in-memory scope keeps the wipe-on-change working within this page load.
  }
}

/**
 * Wipes run one after another, and `wipesDone()` is what a resolution waits for before it opens the console: signing
 * out and straight back in (same account, so no wipe of its own) must not start the console while the sign-out's
 * wipe is still deleting the databases it is about to open (the replica then starts empty and downloads again).
 */
let wipeChain: Promise<void> = Promise.resolve();

function wipeLocal(): Promise<void> {
  wipeChain = wipeChain.then(async () => {
    try {
      await wipeAllLocalData();
    } catch (e) {
      // A failed wipe must not keep the person out; sync/ scopes its stores by company anyway.
      console.error('Wiping local data failed', e);
    }
  });
  return wipeChain;
}

function wipesDone(): Promise<void> {
  return wipeChain;
}

/** The same account with the same visible details (what screens read from `user`). */
function sameUser(a: User | null, b: User | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.email === b.email && a.phone === b.phone && a.updated_at === b.updated_at
    && a.email_confirmed_at === b.email_confirmed_at;
}

/** Memberships, with one quiet retry on a network error (a flaky connection right after sign-in is common). */
async function loadMemberships(): Promise<Membership[]> {
  try {
    return await fetchMemberships();
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    await new Promise((r) => setTimeout(r, 1500));
    return fetchMemberships();
  }
}

/**
 * Signs this browser out now (supabase-js `scope: 'local'`: iPhones and other browsers stay signed in), without
 * pushing pending edits first. SIGNED_OUT then wipes local data. For "Sign out anyway" and after deleting the account.
 */
export async function signOutNow(): Promise<void> {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (e) {
    console.error('Sign-out failed', e);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [core, setCore] = useState<Core>(LOADING);
  const coreRef = useRef<Core>(LOADING);
  const userRef = useRef<User | null>(null);
  /** Bumped by every resolution and sign-out; a stale async answer compares and drops itself. */
  const generation = useRef(0);
  const lastCheck = useRef(0);

  const commit = useCallback((next: Core | ((prev: Core) => Core)) => {
    const value = typeof next === 'function' ? next(coreRef.current) : next;
    coreRef.current = value;
    setCore(value);
  }, []);

  /**
   * A fresh copy of the same user (supabase-js repeats SIGNED_IN on every tab focus and refreshes the token hourly):
   * only a real change is committed, so the console under `useAuth()` doesn't re-render for nothing.
   */
  const adoptUser = useCallback((user: User) => {
    userRef.current = user;
    if (!sameUser(coreRef.current.user, user)) commit((c) => ({ ...c, user }));
  }, [commit]);

  /**
   * Reads memberships for `user` and settles the status. `quiet`: keep showing the current state while checking,
   * and keep it if the check can't reach the server (iOS keeps working with the company it knows).
   */
  const resolve = useCallback(async (user: User, quiet: boolean) => {
    const gen = ++generation.current;
    userRef.current = user;
    if (!quiet) commit((c) => ({ ...c, status: 'loading', user, error: null }));
    else adoptUser(user);
    let list: Membership[];
    try {
      list = await loadMemberships();
    } catch (e) {
      if (gen !== generation.current) return;
      // A refused session was already signed out by the RPC helper; SIGNED_OUT takes it from here.
      if (isSessionFailure(e)) return;
      const settled = coreRef.current.status !== 'loading';
      if (quiet && settled) {
        // Keep the state the person is in (iOS keeps working with the company it knows); outside the console the
        // screen can say why the check didn't go through.
        if (coreRef.current.status !== 'ready') commit((c) => ({ ...c, error: supabaseErrorText(e) }));
        return;
      }
      commit((c) => ({ ...c, status: 'loading', user, error: supabaseErrorText(e) }));
      return;
    }
    if (gen !== generation.current) return;
    lastCheck.current = Date.now();

    const decision = decideMembership(list);
    const prev = coreRef.current.session;
    let ctx: SessionCtx | null = decision.status === 'ready' ? sessionCtxFrom(user, decision.membership) : null;
    // Same values → same object, so screens and sync keyed on the session don't restart.
    if (ctx && sameSessionCtx(prev, ctx)) ctx = prev;
    const nextScope = scopeKey(ctx);
    if (nextScope !== readScope()) {
      // Another account, company or role (or the company is gone): take the company screens down first, then wipe
      // what this browser holds, and only then show the new state.
      if (coreRef.current.status === 'ready') commit((c) => ({ ...c, status: 'loading', session: null, error: null }));
      await wipeLocal();
      if (gen !== generation.current) return;
      writeScope(nextScope);
    } else {
      // Same scope: nothing to wipe, but a sign-out's wipe may still be running.
      await wipesDone();
      if (gen !== generation.current) return;
    }
    const current = coreRef.current;
    const memberships = JSON.stringify(list) === JSON.stringify(current.memberships) ? current.memberships : list;
    const nextUser = sameUser(current.user, user) ? current.user : user;
    // A periodic check that changed nothing doesn't re-render the console.
    if (current.status === decision.status && current.session === ctx && current.memberships === memberships
      && current.user === nextUser && current.error === null) return;
    commit({ status: decision.status, user: nextUser, session: ctx, memberships, error: null });
  }, [adoptUser, commit]);

  const handleSignedOut = useCallback(async () => {
    const gen = ++generation.current;
    userRef.current = null;
    const hadCompany = coreRef.current.status === 'ready';
    commit(SIGNED_OUT);
    forgetPendingAuth();
    if (hadCompany || readScope() !== null) {
      // The scope is forgotten only once the wipe is done (a tab closed halfway wipes again on the next load), and
      // only if nobody signed in meanwhile: that resolution waited for this wipe and owns the scope now.
      await wipeLocal();
      if (gen === generation.current) writeScope(null);
    }
  }, [commit]);

  const handleEvent = useCallback((event: AuthChangeEvent, session: Session | null, finishing: boolean) => {
    switch (event) {
      case 'INITIAL_SESSION':
        if (!session) {
          // No stored session: anything left from an earlier one (expired while the tab was closed) goes.
          void handleSignedOut();
        } else if (finishing) {
          commit((c) => (c.status === 'loading' ? SIGNED_OUT : c));
        } else {
          void resolve(session.user, false);
        }
        return;
      case 'SIGNED_IN':
      case 'PASSWORD_RECOVERY':
        if (!session || finishing) return;
        // supabase-js repeats SIGNED_IN when a tab regains focus; the same account needs no new resolution.
        if (userRef.current?.id === session.user.id && coreRef.current.status !== 'signedOut') {
          adoptUser(session.user);
          return;
        }
        void resolve(session.user, false);
        return;
      case 'TOKEN_REFRESHED':
        if (!session || finishing || coreRef.current.status === 'signedOut') return;
        adoptUser(session.user);
        return;
      case 'USER_UPDATED':
        if (!session || finishing || coreRef.current.status === 'signedOut') return;
        void resolve(session.user, true);
        return;
      case 'SIGNED_OUT':
        void handleSignedOut();
        return;
      default:
        return;
    }
  }, [adoptUser, commit, handleSignedOut, resolve]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // Read the gate now (the event belongs to the flow that is running now), but act outside supabase-js's auth
      // lock: resolving calls RPCs, which read the session again.
      const finishing = isFinishingSignIn();
      window.setTimeout(() => handleEvent(event, session, finishing), 0);
    });
    // A verified code's password step is over: the session now counts (if the step didn't sign out again).
    const offGate = onFinishingReleased(() => {
      void supabase.auth.getSession().then(({ data: { session } }) => {
        if (session && coreRef.current.status === 'signedOut') void resolve(session.user, false);
      });
    });
    return () => {
      data.subscription.unsubscribe();
      offGate();
    };
  }, [handleEvent, resolve]);

  const refreshMemberships = useCallback(async () => {
    const user = userRef.current;
    if (!user) return;
    // Always behind the current screen (the Retry / paused screens show their own "Checking…"): a settled console
    // must not be replaced by the loading page on every check. A real change of company or role still takes the
    // console down before the wipe (see `resolve`).
    await resolve(user, true);
  }, [resolve]);

  // Role changes and removals made elsewhere show up when the tab comes back into view, and a failed check retries
  // as soon as the browser is online again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const status = coreRef.current.status;
      if (status === 'signedOut' || status === 'loading') return;
      if (Date.now() - lastCheck.current < MEMBERSHIP_CHECK_INTERVAL_MS) return;
      void refreshMemberships();
    };
    const onOnline = () => {
      if (coreRef.current.status === 'loading' && coreRef.current.error) void refreshMemberships();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [refreshMemberships]);

  const signOut = useCallback(async () => {
    // Push pending edits first (sync registers the flusher; 5 s at most), then sign out this browser only.
    await flushPending(5000);
    await signOutNow();
  }, []);

  const value = useMemo<AuthState>(
    () => ({ ...core, refreshMemberships, signOut }),
    [core, refreshMemberships, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** The ready session; only call under a route that requires a company (throws otherwise). */
export function useSession(): SessionCtx {
  const { session } = useAuth();
  if (!session) throw new Error('useSession() outside a signed-in company route');
  return session;
}
